/**
 * Collision detection geometry primitives.
 * Re-exports core SAT functions from bathroom-analyzer/pipeline/geometry.js
 * (single source of truth) plus additional helpers.
 */

const {
  calculateCorners,
  calculateClearanceCorners,
  calculateRectCorners,
  doPolygonsOverlap: _doPolygonsOverlap,
  getEdges,
  projectPolygon,
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,
} = require('../bathroom-analyzer/pipeline/geometry');
const {
  BASIN_CLEARANCE_WIDTH,
  getClearanceConfig,
} = require('./clearanceConfig');

// 1/8 inch tolerance for floor plan collision checks — absorbs floating-point
// drift from pipeline expansion, coordinate conversion, and rounding.
const COLLISION_TOLERANCE = 0.125 * 0.0254; // 1/8" in meters ≈ 0.003175m

/**
 * SAT overlap check with built-in tolerance for floor plan collisions.
 * Overlaps smaller than COLLISION_TOLERANCE are ignored.
 */
function doPolygonsOverlap(poly1, poly2) {
  return _doPolygonsOverlap(poly1, poly2, COLLISION_TOLERANCE);
}

/**
 * SAT overlap check with no tolerance (exact).
 * Use when precise overlap detection is needed (e.g. pipeline expansion).
 */
function doPolygonsOverlapExact(poly1, poly2) {
  return _doPolygonsOverlap(poly1, poly2, 0);
}

/**
 * Get physical corners for a standard room object.
 * Converts { center, dimensions, rotation } to 4 XZ corner points.
 *
 * Corner bathtubs with a `tub-surround-polygon` return the polygon shape
 * (pentagon) instead of the rectangular bounding box, so collision detection
 * matches the actual footprint rather than the rotated-diamond bounding box.
 *
 * Negate rotation to match three.js Y-rotation convention (front maps to
 * (sin, cos)). calculateCorners uses standard-math rotation where +Z maps
 * to (-sin, cos) — same workaround as getObjectClearanceCorners below.
 */
function getObjectCorners(obj) {
  const poly = obj['tub-surround-polygon'];
  const objType = (obj.type || '').toLowerCase();
  if (
    (objType === 'bathtub' || objType === 'tub-support-box') &&
    poly?.points?.length >= 5
  ) {
    const I2M = 0.0254;
    const rotAngle = -(obj.rotation?.y || 0);
    const cos = Math.cos(rotAngle);
    const sin = Math.sin(rotAngle);
    return poly.points.map((p) => ({
      x: obj.center.x + p.x * I2M * cos - p.z * I2M * sin,
      z: obj.center.z + p.x * I2M * sin + p.z * I2M * cos,
    }));
  }

  // A corner filler is a WEDGE (`buildCornerFillCountertop`, the angled-run
  // bend fillers), outlined by `polygonCorners` in room coordinates and always
  // convex. Its `center` / `dimensions` are only the box around it, which
  // overlaps both legs beside it — so every angled corner reported a collision
  // that was not there.
  if (objType === 'corner-fill-cabinet' && obj.polygonCorners?.length >= 3) {
    return obj.polygonCorners.map((p) => ({ x: p.x, z: p.z }));
  }

  return calculateCorners(
    { x: obj.center.x, z: obj.center.z },
    obj.dimensions.x,
    obj.dimensions.z,
    -(obj.rotation?.y || 0)
  );
}

/**
 * Get clearance zone corners for an object based on its clearance config.
 * Back edge aligns with physical back edge, front extends into the room.
 *
 * Rotation convention: rotation.y = atan2(normal.x, normal.z) where normal
 * is the wall's inward normal. Front direction = (sin(rot), cos(rot)).
 *
 * @param {Object} obj - Object with center, dimensions, rotation
 * @param {Object} config - From getClearanceConfig
 */
/**
 * How wide an object's clearance rectangle is: the config's FIXED access width
 * when it declares one (a 24" shower opening, a bathroom basin in a vanity),
 * else the object's own width plus its side clearances.
 *
 * Shared with `getCabinetClearanceFootprints`, so the zone a sink draws for
 * itself and the one its cabinet draws for it cannot disagree.
 */
function clearanceWidthFor(obj, config) {
  return (
    config.clearanceWidth ||
    _dim(obj?.dimensions, 'x') + (config.sideClearance || 0) * 2
  );
}

function getObjectClearanceCorners(obj, config, sideOffset) {
  const sideClearance = config.sideClearance || 0;
  const frontClearance = config.frontClearance || 0;
  let clearanceWidth = clearanceWidthFor(obj, config);
  // Compact profile: a fixed access width (the 24" basin / shower window)
  // never exceeds the fixture itself plus its side clearance — a 20"
  // pedestal sink in a tight bath is used from directly in front of it.
  if (
    config.clearanceProfile === 'compact' &&
    config.clearanceWidth &&
    obj.dimensions?.x > 0
  ) {
    clearanceWidth = Math.min(
      clearanceWidth,
      _dim(obj.dimensions, 'x') + sideClearance * 2
    );
  }
  // Through `_dim`: `assembleVanity` emits a sink as `{width, height, depth}`
  // with no `.z`, and reading it raw made `clearanceDepth` NaN — four NaN
  // corners, which draw nothing on a canvas and overlap nothing in SAT. The
  // zone did not shrink, it silently ceased to exist (9 sinks in the corpus).
  const physicalDepth = _dim(obj.dimensions, 'z');
  const clearanceDepth = physicalDepth + frontClearance;

  // For slideable clearance (tubs/showers), offset the clearance zone
  // along the physical body's front edge. sideOffset is in meters,
  // positive = slide right along front edge, negative = slide left.
  let centerX = obj.center.x;
  let centerZ = obj.center.z;
  if (sideOffset && sideOffset !== 0) {
    const rot = obj.rotation?.y || 0;
    // Wall direction (tangent) = perpendicular to front normal
    const wallDirX = Math.cos(rot);
    const wallDirZ = -Math.sin(rot);
    centerX += wallDirX * sideOffset;
    centerZ += wallDirZ * sideOffset;
  }

  // Negate rotation: calculateClearanceCorners uses standard math rotation
  // where +Z maps to (-sin, cos), but our convention has front at (sin, cos).
  return calculateClearanceCorners(
    { x: centerX, z: centerZ },
    physicalDepth,
    clearanceWidth,
    clearanceDepth,
    -(obj.rotation?.y || 0)
  );
}

/**
 * Identify a sink-typed child of a vanity cabinet OR a kitchen base
 * cabinet, excluding faucets (which match 'sink' as a substring in
 * some categorizations).
 */
function isSinkChild(obj) {
  if (!obj) return false;
  const type = (obj.type || '').toLowerCase();
  if (type === 'sink' || type === 'kitchen-sink') return true;
  if (obj.category) {
    if (obj.category.sink || obj.category['kitchen-sink']) return true;
  }
  return false;
}

/**
 * Read a dimension that may live under either `.x/.y/.z` (most fixtures) or
 * `.width/.height/.depth` (sinks/countertops emitted by `assembleVanity`).
 * Both shapes coexist in the room-objects model.
 */
function _dim(dimensions, axis) {
  if (!dimensions) return 0;
  const xyz = { x: 'x', y: 'y', z: 'z' }[axis];
  const named = { x: 'width', y: 'height', z: 'depth' }[axis];
  const v = dimensions[xyz];
  if (typeof v === 'number') return v;
  const n = dimensions[named];
  return typeof n === 'number' ? n : 0;
}

/**
 * Get per-sink clearance footprints for a `lower-cabinet`.
 *
 * Each footprint is a `sinkWidth × frontClearance` rectangle (30" deep for
 * the default sink/`double-sink`; an S5 clearance-shrink can lower this
 * to 24" via `customFrontClearance`). The back edge sits flush
 * against the cabinet's front face; the front edge is `frontClearance`
 * forward. Width is the sink's lateral dimension. The rectangle is centered
 * laterally on the sink's actual world position projected onto the cabinet's
 * wall axis — so off-center sinks produce off-center footprints.
 *
 * The cabinet body itself is not part of any footprint (it's already caught
 * by physical-vs-physical checks). Returns `[]` if the cabinet has no
 * `fixtureGroupId` or no sink children — such a cabinet claims no walking
 * space.
 *
 * @param {Object} cabinet - lower-cabinet object
 * @param {Object[]} allObjects - All room objects (used to find sink children)
 * @returns {Polygon[]} Array of 4-corner polygons in world coords
 */
function getCabinetClearanceFootprints(cabinet, allObjects) {
  const groupId = cabinet.fixtureGroupId;
  if (!groupId) return [];

  const sinks = (allObjects || []).filter(
    (o) => o.fixtureGroupId === groupId && isSinkChild(o)
  );
  if (sinks.length === 0) return [];

  const cabinetRot = cabinet.rotation?.y || 0;
  // Front direction (sin, cos) per the standard rotation convention used
  // throughout collision/. Wall (lateral) direction is perpendicular.
  const frontX = Math.sin(cabinetRot);
  const frontZ = Math.cos(cabinetRot);
  const wallX = Math.cos(cabinetRot);
  const wallZ = -Math.sin(cabinetRot);
  const cabinetDepth = _dim(cabinet.dimensions, 'z');
  // Cabinet front face center in world coords (uses the passed-in cabinet,
  // which during drag is the tentative new position).
  const frontFaceX = cabinet.center.x + (cabinetDepth / 2) * frontX;
  const frontFaceZ = cabinet.center.z + (cabinetDepth / 2) * frontZ;

  // For lateralOffset we need the sink's offset RELATIVE TO THE CABINET in
  // a consistent frame. During drag, the passed-in cabinet has a tentative
  // (new) center but the sinks in allObjects still hold the previous frame's
  // positions. Computing offset against the passed-in cabinet would be off
  // by exactly the drag delta along the wall axis — which makes the
  // clearance footprint stay anchored to the sink's world position instead
  // of following the cabinet, defeating any snap push.
  //
  // Look up the cabinet in allObjects to get its stored center (same frame
  // as the sinks); the offset computed from those two is the true sink-
  // local offset in the cabinet's own frame. Falls back to the passed-in
  // cabinet when the lookup misses (non-drag callers, etc.).
  const storedCabinet =
    (allObjects || []).find((o) => o && o.id === cabinet.id) || cabinet;
  const cabinetRefX = storedCabinet.center?.x ?? cabinet.center.x;
  const cabinetRefZ = storedCabinet.center?.z ?? cabinet.center.z;

  const footprints = [];
  for (const sink of sinks) {
    // Resolved per sink (not the raw type table) so a compact-profile
    // sink reports its code-minimum front clearance here too.
    const sinkConfig = getClearanceConfig(sink);
    const frontClearance = sinkConfig.frontClearance || 0;
    if (frontClearance <= 0) continue;

    // Basin clearance width is fixed (independent of the basin's actual
    // physical width and of the cabinet's width) — except under the compact
    // profile (scan-time, see clearance-resolution.js), where it never
    // exceeds the cabinet: a 21" vanity built flush to a tub is used from
    // in front of its own doors, not from 1.5" inside the tub.
    // The same width the sink would draw for itself, never below the basin
    // default — so a wide kitchen basin gets a zone that covers it, while a
    // bathroom basin (whose own width is at or under the default) keeps the
    // fixed 24" this constant was written for. The two paths agree because
    // both ask `clearanceWidthFor`.
    const sinkWidth =
      sinkConfig.clearanceProfile === 'compact'
        ? Math.min(BASIN_CLEARANCE_WIDTH, _dim(cabinet.dimensions, 'x'))
        : Math.max(BASIN_CLEARANCE_WIDTH, clearanceWidthFor(sink, sinkConfig));
    // Project the sink's world position onto the cabinet's lateral axis to
    // recover its lateral offset from the cabinet center (using the stored
    // cabinet so both points are sampled at the same frame).
    const dx = sink.center.x - cabinetRefX;
    const dz = sink.center.z - cabinetRefZ;
    const lateralOffset = dx * wallX + dz * wallZ;

    // Footprint center: cabinet front face + frontClearance/2 forward
    // + lateralOffset along wall.
    const centerX =
      frontFaceX + (frontClearance / 2) * frontX + lateralOffset * wallX;
    const centerZ =
      frontFaceZ + (frontClearance / 2) * frontZ + lateralOffset * wallZ;

    // Negate rotation: calculateCorners uses standard math rotation where
    // +Z maps to (-sin, cos), but our convention has front at (sin, cos).
    footprints.push(
      calculateCorners(
        { x: centerX, z: centerZ },
        sinkWidth,
        frontClearance,
        -cabinetRot
      )
    );
  }
  return footprints;
}

/**
 * Build per-basin clearance footprints for a `double-sink` from its
 * `sinkLateralOffsets` array (set by pipeline conversion). Each footprint
 * is a `BASIN_CLEARANCE_WIDTH × frontClearance` rectangle, back at the
 * cabinet's front face, centered laterally on its offset along wallDir.
 *
 * Mirrors the rectangle geometry produced by `getCabinetClearanceFootprints`
 * (which derives offsets from sink children's world positions) so the
 * pipeline-side double-sink fixture and the apply-time cabinet+sinks
 * decomposition see the same clearance shape. Without this, the pipeline-
 * format double-sink falls through to a single full-cabinet-width
 * rectangle, which spuriously overlaps adjacent fixtures in the modal
 * preview that disappear after assembleVanity decomposes the cabinet.
 *
 * Falls back to `null` (caller uses the default single-rect path) if
 * `sinkLateralOffsets` is missing — legacy double-sinks without per-basin
 * data still get the old behavior.
 */
function getDoubleSinkFootprints(obj, config) {
  const offsets = Array.isArray(obj.sinkLateralOffsets)
    ? obj.sinkLateralOffsets
    : null;
  if (!offsets || offsets.length === 0) return null;
  const frontClearance = config.frontClearance || 0;
  if (frontClearance <= 0) return [];

  const rot = obj.rotation?.y || 0;
  const frontX = Math.sin(rot);
  const frontZ = Math.cos(rot);
  const wallX = Math.cos(rot);
  const wallZ = -Math.sin(rot);
  const depth = _dim(obj.dimensions, 'z');
  const frontFaceX = obj.center.x + (depth / 2) * frontX;
  const frontFaceZ = obj.center.z + (depth / 2) * frontZ;

  const footprints = [];
  for (const offset of offsets) {
    const cx = frontFaceX + (frontClearance / 2) * frontX + offset * wallX;
    const cz = frontFaceZ + (frontClearance / 2) * frontZ + offset * wallZ;
    footprints.push(
      calculateCorners(
        { x: cx, z: cz },
        BASIN_CLEARANCE_WIDTH,
        frontClearance,
        -rot
      )
    );
  }
  return footprints;
}

/**
 * Get clearance footprints for any object as a polygon array.
 *
 * - `lower-cabinet`: per-sink rectangles via `getCabinetClearanceFootprints`.
 * - `double-sink`: per-basin rectangles via `getDoubleSinkFootprints`
 *   (uses `sinkLateralOffsets` from pipeline conversion).
 * - All other objects: the single rectangle from `getObjectClearanceCorners`,
 *   wrapped in a 1-element array. Returns `[]` if the object has no
 *   meaningful clearance.
 */
function getObjectClearanceFootprints(obj, config, allObjects) {
  const type = (obj.type || '').toLowerCase();
  // Corner bathtubs: the standard clearance rectangle extends behind the tub
  // into walls. Instead, compute a clearance zone that starts at the tub
  // center and extends forward (into the room along the diagonal).
  if (
    (type === 'bathtub' || type === 'tub-support-box') &&
    obj['tub-surround-polygon']?.points?.length >= 5
  ) {
    const frontClearance = config.frontClearance || 0;
    const clearanceWidth = config.clearanceWidth || 0;
    if (frontClearance <= 0) return [];
    const rot = obj.rotation?.y || 0;
    const frontX = Math.sin(rot);
    const frontZ = Math.cos(rot);
    const cx = obj.center.x + (frontClearance / 2) * frontX;
    const cz = obj.center.z + (frontClearance / 2) * frontZ;
    return [
      calculateCorners({ x: cx, z: cz }, clearanceWidth, frontClearance, -rot),
    ];
  }
  if (type === 'lower-cabinet') {
    return getCabinetClearanceFootprints(obj, allObjects);
  }
  if (type === 'double-sink') {
    const fps = getDoubleSinkFootprints(obj, config);
    if (fps !== null) return fps;
    // Fall through to default single-rect path for legacy data without
    // sinkLateralOffsets.
  }
  if (
    !(config.frontClearance > 0) &&
    !(config.sideClearance > 0) &&
    !config.clearanceWidth
  ) {
    return [];
  }
  const single = getObjectClearanceCorners(obj, config, obj.clearanceOffset);
  return single && single.length >= 4 ? [single] : [];
}

module.exports = {
  // Core SAT primitives (from bathroom-analyzer)
  calculateCorners,
  calculateClearanceCorners,
  calculateRectCorners,
  doPolygonsOverlap,
  doPolygonsOverlapExact,
  COLLISION_TOLERANCE,
  getEdges,
  projectPolygon,
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,

  // Object-level helpers
  getObjectCorners,
  getObjectClearanceCorners,
  getCabinetClearanceFootprints,
  getObjectClearanceFootprints,
};
